/*!
 * Parenting Optimize - Application Framework
 * File: parenting-app.js
 * Version: 1.0.0
 *
 * Purpose:
 * UI controller, routing, state, storage, rendering, child profile handling,
 * dashboard shell, notifications, modals, feedback UI, and utilities.
 *
 * This file intentionally contains no parenting advice and no methodology logic.
 * Parenting decisions belong in parenting-engine.js.
 * Parenting knowledge/content belongs in parenting-knowledge.js.
 */

(function (window, document) {
  "use strict";

  // ==========================================================================
  // 01. GLOBAL NAMESPACE
  // ==========================================================================

  const PO = (window.PO = window.PO || {});

  PO.version = "1.0.0";

  // ==========================================================================
  // 02. CONSTANTS
  // ==========================================================================

  PO.Constants = Object.freeze({
    appName: "Parenting Optimize",
    namespace: "PO",

    selectors: {
      root: "#po-app",
      screen: "[data-po-screen]",
      action: "[data-po-action]",
      field: "[data-po-field]",
      choice: "[data-po-choice]",
      modal: "#po-modal",
      toast: "#po-toast",
      progressBar: "#po-progress-bar",
      progressText: "#po-progress-text",
      childList: "#po-child-list",
      childSwitcher: "#po-child-switcher",
      dashboard: "#po-dashboard",
      plan: "#po-plan",
      feedback: "#po-feedback",
      navBack: "#po-nav-back",
      navNext: "#po-nav-next"
    },

    storageKeys: {
      parent: "PO_PARENT",
      family: "PO_FAMILY",
      children: "PO_CHILDREN",
      activeChildId: "PO_ACTIVE_CHILD_ID",
      history: "PO_HISTORY",
      feedback: "PO_FEEDBACK",
      session: "PO_SESSION",
      settings: "PO_SETTINGS",
      onboarding: "PO_ONBOARDING"
    },

    routes: {
      welcome: "welcome",
      parentProfile: "parent-profile",
      familyProfile: "family-profile",
      childProfile: "child-profile",
      childStrengths: "child-strengths",
      mainChallenge: "main-challenge",
      dashboard: "dashboard",
      situationPicker: "situation-picker",
      situationDetail: "situation-detail",
      plan: "plan",
      feedback: "feedback",
      insights: "insights",
      settings: "settings"
    },

    defaultScreens: [
      "welcome",
      "parent-profile",
      "family-profile",
      "child-profile",
      "child-strengths",
      "main-challenge",
      "dashboard"
    ],

    childColors: [
      "sage",
      "clay",
      "blue",
      "sand",
      "lavender",
      "olive",
      "rose",
      "stone"
    ],

    notificationTypes: ["info", "success", "warning", "error"],

    maxChildrenV1: 8
  });

  // ==========================================================================
  // 03. UTILITIES
  // ==========================================================================

  PO.Utils = {
    uid(prefix = "po") {
      return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    },

    now() {
      return new Date().toISOString();
    },

    todayKey() {
      return new Date().toISOString().slice(0, 10);
    },

    isObject(value) {
      return value !== null && typeof value === "object" && !Array.isArray(value);
    },

    clone(value) {
      try {
        return JSON.parse(JSON.stringify(value));
      } catch (err) {
        console.warn("PO.Utils.clone failed", err);
        return value;
      }
    },

    merge(target, source) {
      const output = PO.Utils.clone(target || {});
      if (!PO.Utils.isObject(source)) return output;

      Object.keys(source).forEach((key) => {
        if (PO.Utils.isObject(source[key])) {
          output[key] = PO.Utils.merge(output[key], source[key]);
        } else {
          output[key] = source[key];
        }
      });

      return output;
    },

    debounce(fn, delay = 250) {
      let timer = null;
      return function debounced(...args) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), delay);
      };
    },

    throttle(fn, delay = 250) {
      let locked = false;
      return function throttled(...args) {
        if (locked) return;
        locked = true;
        fn.apply(this, args);
        window.setTimeout(() => {
          locked = false;
        }, delay);
      };
    },

    escapeHTML(value) {
      return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    },

    titleCase(value) {
      return String(value || "")
        .replace(/[-_]/g, " ")
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    },

    compactText(value, fallback = "Not provided") {
      const str = String(value || "").trim();
      return str.length ? str : fallback;
    },

    pluralize(count, singular, plural) {
      return Number(count) === 1 ? singular : plural || `${singular}s`;
    },

    clamp(value, min, max) {
      return Math.min(Math.max(Number(value), min), max);
    },

    readDataset(el, name, fallback = null) {
      if (!el || !el.dataset) return fallback;
      return el.dataset[name] ?? fallback;
    },

    query(selector, root = document) {
      return root.querySelector(selector);
    },

    queryAll(selector, root = document) {
      return Array.from(root.querySelectorAll(selector));
    },

    safeCall(fn, fallback = null) {
      try {
        return typeof fn === "function" ? fn() : fallback;
      } catch (err) {
        console.error("PO safeCall error", err);
        return fallback;
      }
    },

    wait(ms = 0) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    },

    formatTimeOfDay() {
      const hour = new Date().getHours();
      if (hour < 12) return "morning";
      if (hour < 18) return "afternoon";
      return "evening";
    },

    readableDate(iso) {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric"
        });
      } catch {
        return "";
      }
    }
  };

  // ==========================================================================
  // 04. EVENT BUS
  // ==========================================================================

  PO.Events = {
    _events: {},

    on(name, handler) {
      if (!name || typeof handler !== "function") return () => {};
      this._events[name] = this._events[name] || [];
      this._events[name].push(handler);

      return () => this.off(name, handler);
    },

    off(name, handler) {
      if (!this._events[name]) return;
      this._events[name] = this._events[name].filter((fn) => fn !== handler);
    },

    emit(name, payload = {}) {
      if (!this._events[name]) return;
      this._events[name].forEach((handler) => {
        try {
          handler(payload);
        } catch (err) {
          console.error(`PO.Events handler failed for ${name}`, err);
        }
      });
    },

    once(name, handler) {
      const off = this.on(name, (payload) => {
        off();
        handler(payload);
      });
      return off;
    }
  };

  // ==========================================================================
  // 05. STORAGE
  // ==========================================================================

  PO.Storage = {
    isAvailable() {
      try {
        const testKey = "__po_test__";
        window.localStorage.setItem(testKey, "1");
        window.localStorage.removeItem(testKey);
        return true;
      } catch {
        return false;
      }
    },

    get(key, fallback = null) {
      if (!this.isAvailable()) return fallback;
      try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return fallback;
        return JSON.parse(raw);
      } catch (err) {
        console.warn("PO.Storage.get failed", key, err);
        return fallback;
      }
    },

    set(key, value) {
      if (!this.isAvailable()) return false;
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        PO.Events.emit("storage:changed", { key, value });
        return true;
      } catch (err) {
        console.warn("PO.Storage.set failed", key, err);
        return false;
      }
    },

    remove(key) {
      if (!this.isAvailable()) return false;
      try {
        window.localStorage.removeItem(key);
        PO.Events.emit("storage:removed", { key });
        return true;
      } catch (err) {
        console.warn("PO.Storage.remove failed", key, err);
        return false;
      }
    },

    clearAll() {
      Object.values(PO.Constants.storageKeys).forEach((key) => this.remove(key));
    },

    push(key, item) {
      const list = this.get(key, []);
      const next = Array.isArray(list) ? list.concat([item]) : [item];
      this.set(key, next);
      return next;
    },

    patch(key, patch) {
      const current = this.get(key, {});
      const next = PO.Utils.merge(current, patch);
      this.set(key, next);
      return next;
    }
  };

  // ==========================================================================
  // 06. STATE
  // ==========================================================================

  PO.State = {
    data: {
      route: PO.Constants.routes.welcome,
      previousRoute: null,
      parent: {},
      family: {},
      children: [],
      activeChildId: null,
      activeSituation: null,
      activePlan: null,
      answers: {},
      session: {
        startedAt: null,
        updatedAt: null,
        completedOnboarding: false
      },
      ui: {
        loading: false,
        modalOpen: false,
        drawerOpen: false,
        progress: 0
      }
    },

    init() {
      this.data.parent = PO.Storage.get(PO.Constants.storageKeys.parent, {});
      this.data.family = PO.Storage.get(PO.Constants.storageKeys.family, {});
      this.data.children = PO.Storage.get(PO.Constants.storageKeys.children, []);
      this.data.activeChildId = PO.Storage.get(PO.Constants.storageKeys.activeChildId, null);
      this.data.session = PO.Storage.get(PO.Constants.storageKeys.session, {
        startedAt: PO.Utils.now(),
        updatedAt: PO.Utils.now(),
        completedOnboarding: false
      });

      if (!this.data.activeChildId && this.data.children[0]) {
        this.data.activeChildId = this.data.children[0].id;
      }

      this.persistSession();
      PO.Events.emit("state:init", this.snapshot());
    },

    snapshot() {
      return PO.Utils.clone(this.data);
    },

    get(path, fallback = null) {
      if (!path) return this.data;
      const parts = String(path).split(".");
      let current = this.data;
      for (const part of parts) {
        if (current && Object.prototype.hasOwnProperty.call(current, part)) {
          current = current[part];
        } else {
          return fallback;
        }
      }
      return current;
    },

    set(path, value, options = {}) {
      const parts = String(path).split(".");
      let current = this.data;

      while (parts.length > 1) {
        const part = parts.shift();
        current[part] = current[part] || {};
        current = current[part];
      }

      current[parts[0]] = value;

      if (!options.silent) {
        PO.Events.emit("state:changed", {
          path,
          value,
          state: this.snapshot()
        });
      }

      return value;
    },

    update(path, patch) {
      const current = this.get(path, {});
      const next = PO.Utils.merge(current, patch);
      this.set(path, next);
      return next;
    },

    persistCore() {
      PO.Storage.set(PO.Constants.storageKeys.parent, this.data.parent);
      PO.Storage.set(PO.Constants.storageKeys.family, this.data.family);
      PO.Storage.set(PO.Constants.storageKeys.children, this.data.children);
      PO.Storage.set(PO.Constants.storageKeys.activeChildId, this.data.activeChildId);
      this.persistSession();
    },

    persistSession() {
      this.data.session.updatedAt = PO.Utils.now();
      PO.Storage.set(PO.Constants.storageKeys.session, this.data.session);
    },

    reset() {
      PO.Storage.clearAll();
      this.data.parent = {};
      this.data.family = {};
      this.data.children = [];
      this.data.activeChildId = null;
      this.data.activeSituation = null;
      this.data.activePlan = null;
      this.data.answers = {};
      this.data.session = {
        startedAt: PO.Utils.now(),
        updatedAt: PO.Utils.now(),
        completedOnboarding: false
      };
      PO.Events.emit("state:reset", this.snapshot());
    }
  };

  // ==========================================================================
  // 07. ROUTER
  // ==========================================================================

  PO.Router = {
    history: [],

    current() {
      return PO.State.get("route", PO.Constants.routes.welcome);
    },

    navigate(route, options = {}) {
      if (!route) return;
      const previous = this.current();

      if (previous !== route && !options.replace) {
        this.history.push(previous);
      }

      PO.State.set("previousRoute", previous, { silent: true });
      PO.State.set("route", route);

      if (options.scroll !== false) {
        PO.UI.scrollToTop();
      }

      PO.Events.emit("route:changed", {
        from: previous,
        to: route,
        options
      });

      PO.App.render();
    },

    back(fallback = PO.Constants.routes.dashboard) {
      const previous = this.history.pop();
      this.navigate(previous || fallback, { replace: true });
    },

    restart() {
      this.history = [];
      this.navigate(PO.Constants.routes.welcome, { replace: true });
    }
  };

  // ==========================================================================
  // 08. VALIDATION
  // ==========================================================================

  PO.Validation = {
    required(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },

    minLength(value, length) {
      return String(value || "").trim().length >= length;
    },

    childName(value) {
      const clean = String(value || "").trim();
      if (!clean) return "Please enter a name or nickname.";
      if (clean.length > 40) return "Please keep the name shorter.";
      return true;
    },

    age(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return "Please enter an age.";
      if (num < 1 || num > 25) return "Please enter an age between 1 and 25.";
      return true;
    },

    showError(fieldName, message) {
      const el = document.querySelector(`[data-po-error="${fieldName}"]`);
      if (el) {
        el.textContent = message || "";
        el.hidden = !message;
      }
    },

    clearErrors(root = document) {
      PO.Utils.queryAll("[data-po-error]", root).forEach((el) => {
        el.textContent = "";
        el.hidden = true;
      });
    }
  };

  // ==========================================================================
  // 09. UI CORE
  // ==========================================================================

  PO.UI = {
    root() {
      return PO.Utils.query(PO.Constants.selectors.root);
    },

    setHTML(selector, html) {
      const el = PO.Utils.query(selector);
      if (el) el.innerHTML = html;
      return el;
    },

    show(selector) {
      const el = typeof selector === "string" ? PO.Utils.query(selector) : selector;
      if (el) el.hidden = false;
      return el;
    },

    hide(selector) {
      const el = typeof selector === "string" ? PO.Utils.query(selector) : selector;
      if (el) el.hidden = true;
      return el;
    },

    toggle(selector, condition) {
      return condition ? this.show(selector) : this.hide(selector);
    },

    addClass(el, className) {
      if (el) el.classList.add(className);
    },

    removeClass(el, className) {
      if (el) el.classList.remove(className);
    },

    scrollToTop() {
      const root = this.root();
      if (root) {
        root.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    lock() {
      PO.State.set("ui.loading", true);
      document.documentElement.classList.add("po-is-loading");
    },

    unlock() {
      PO.State.set("ui.loading", false);
      document.documentElement.classList.remove("po-is-loading");
    },

    focusFirst(root = document) {
      const target = root.querySelector("input, select, textarea, button, [tabindex]:not([tabindex='-1'])");
      if (target && typeof target.focus === "function") {
        window.setTimeout(() => target.focus(), 30);
      }
    },

    renderScreen(html) {
      const root = this.root();
      if (!root) {
        console.error("PO root container not found. Expected #po-app.");
        return;
      }
      root.innerHTML = html;
      this.focusFirst(root);
      PO.Events.emit("ui:rendered", { route: PO.Router.current() });
    }
  };

  // ==========================================================================
  // 10. COMPONENTS
  // ==========================================================================

  PO.Components = {
    icon(name) {
      const icons = {
        leaf: "⌁",
        child: "◉",
        check: "✓",
        arrow: "→",
        back: "←",
        plus: "+",
        heart: "♡",
        spark: "✦",
        clock: "◷",
        note: "☰"
      };
      return `<span class="po-icon po-icon-${PO.Utils.escapeHTML(name)}" aria-hidden="true">${icons[name] || "•"}</span>`;
    },

    button(label, action, options = {}) {
      const variant = options.variant || "primary";
      const attrs = [
        `class="po-btn po-btn-${variant}${options.block ? " po-btn-block" : ""}"`,
        `data-po-action="${PO.Utils.escapeHTML(action)}"`
      ];

      if (options.value !== undefined) attrs.push(`data-po-value="${PO.Utils.escapeHTML(options.value)}"`);
      if (options.disabled) attrs.push("disabled");

      return `<button ${attrs.join(" ")}>${PO.Utils.escapeHTML(label)}</button>`;
    },

    ghostButton(label, action, options = {}) {
      return this.button(label, action, { ...options, variant: "ghost" });
    },

    card(content, options = {}) {
      const classes = ["po-card"];
      if (options.className) classes.push(options.className);
      return `<section class="${classes.join(" ")}">${content}</section>`;
    },

    eyebrow(text) {
      return `<div class="po-eyebrow">${PO.Utils.escapeHTML(text)}</div>`;
    },

    pageTitle(title, subtitle = "") {
      return `
        <header class="po-page-header">
          <h1>${PO.Utils.escapeHTML(title)}</h1>
          ${subtitle ? `<p>${PO.Utils.escapeHTML(subtitle)}</p>` : ""}
        </header>
      `;
    },

    progress(value, label = "") {
      const pct = PO.Utils.clamp(value, 0, 100);
      return `
        <div class="po-progress-wrap" aria-label="Progress">
          <div class="po-progress-meta">
            <span>${PO.Utils.escapeHTML(label || "Progress")}</span>
            <span>${pct}%</span>
          </div>
          <div class="po-progress-track">
            <div class="po-progress-fill" style="width:${pct}%"></div>
          </div>
        </div>
      `;
    },

    choice(label, value, group, options = {}) {
      const selected = options.selected === value;
      return `
        <button class="po-choice${selected ? " is-selected" : ""}"
          data-po-action="select-choice"
          data-po-group="${PO.Utils.escapeHTML(group)}"
          data-po-value="${PO.Utils.escapeHTML(value)}"
          type="button">
          <span>${PO.Utils.escapeHTML(label)}</span>
          ${selected ? "<small>Selected</small>" : ""}
        </button>
      `;
    },

    choiceGrid(items, group, selected) {
      return `
        <div class="po-choice-grid">
          ${items.map((item) => this.choice(item.label, item.value, group, { selected })).join("")}
        </div>
      `;
    },

    input(label, field, options = {}) {
      const type = options.type || "text";
      const value = options.value || "";
      return `
        <label class="po-field">
          <span>${PO.Utils.escapeHTML(label)}</span>
          <input type="${PO.Utils.escapeHTML(type)}"
            data-po-field="${PO.Utils.escapeHTML(field)}"
            value="${PO.Utils.escapeHTML(value)}"
            placeholder="${PO.Utils.escapeHTML(options.placeholder || "")}"
            ${options.autocomplete ? `autocomplete="${PO.Utils.escapeHTML(options.autocomplete)}"` : ""}
          />
          <small data-po-error="${PO.Utils.escapeHTML(field)}" hidden></small>
        </label>
      `;
    },

    textarea(label, field, options = {}) {
      const value = options.value || "";
      return `
        <label class="po-field">
          <span>${PO.Utils.escapeHTML(label)}</span>
          <textarea data-po-field="${PO.Utils.escapeHTML(field)}"
            placeholder="${PO.Utils.escapeHTML(options.placeholder || "")}">${PO.Utils.escapeHTML(value)}</textarea>
          <small data-po-error="${PO.Utils.escapeHTML(field)}" hidden></small>
        </label>
      `;
    },

    shell(content, options = {}) {
      const route = PO.Router.current();
      const showBack = options.showBack !== false && route !== PO.Constants.routes.welcome;
      return `
        <div class="po-shell" data-po-route="${PO.Utils.escapeHTML(route)}">
          <div class="po-topbar">
            <button class="po-topbar-back" data-po-action="back" ${showBack ? "" : "hidden"} aria-label="Go back">←</button>
            <div class="po-brand">
              <span class="po-brand-mark">⌁</span>
              <span>Parenting Optimize</span>
            </div>
            <button class="po-topbar-link" data-po-action="open-settings" aria-label="Settings">Settings</button>
          </div>
          <main class="po-main">
            ${content}
          </main>
        </div>
      `;
    },

    emptyState(title, text, actionLabel, action) {
      return `
        <div class="po-empty">
          <div class="po-empty-mark">✦</div>
          <h2>${PO.Utils.escapeHTML(title)}</h2>
          <p>${PO.Utils.escapeHTML(text)}</p>
          ${actionLabel ? PO.Components.button(actionLabel, action, { block: true }) : ""}
        </div>
      `;
    },

    childAvatar(child) {
      const initial = (child && child.name ? child.name.charAt(0) : "?").toUpperCase();
      const color = child.color || "sage";
      return `<span class="po-child-avatar po-child-avatar-${PO.Utils.escapeHTML(color)}">${PO.Utils.escapeHTML(initial)}</span>`;
    },

    stat(label, value, helper = "") {
      return `
        <div class="po-stat">
          <strong>${PO.Utils.escapeHTML(value)}</strong>
          <span>${PO.Utils.escapeHTML(label)}</span>
          ${helper ? `<small>${PO.Utils.escapeHTML(helper)}</small>` : ""}
        </div>
      `;
    },

    pill(text, tone = "neutral") {
      return `<span class="po-pill po-pill-${PO.Utils.escapeHTML(tone)}">${PO.Utils.escapeHTML(text)}</span>`;
    }
  };

  // ==========================================================================
  // 11. MODAL + TOAST
  // ==========================================================================

  PO.Modal = {
    open({ title = "", body = "", actions = "" } = {}) {
      let modal = PO.Utils.query("#po-modal");
      if (!modal) {
        modal = document.createElement("div");
        modal.id = "po-modal";
        modal.className = "po-modal";
        document.body.appendChild(modal);
      }

      modal.innerHTML = `
        <div class="po-modal-backdrop" data-po-action="close-modal"></div>
        <div class="po-modal-card" role="dialog" aria-modal="true">
          <button class="po-modal-close" data-po-action="close-modal" aria-label="Close">×</button>
          ${title ? `<h2>${PO.Utils.escapeHTML(title)}</h2>` : ""}
          <div class="po-modal-body">${body}</div>
          ${actions ? `<div class="po-modal-actions">${actions}</div>` : ""}
        </div>
      `;

      modal.hidden = false;
      PO.State.set("ui.modalOpen", true);
      PO.Events.emit("modal:opened", { title });
    },

    close() {
      const modal = PO.Utils.query("#po-modal");
      if (modal) modal.hidden = true;
      PO.State.set("ui.modalOpen", false);
      PO.Events.emit("modal:closed", {});
    }
  };

  PO.Toast = {
    timer: null,

    show(message, type = "info", duration = 2800) {
      if (!PO.Constants.notificationTypes.includes(type)) type = "info";

      let toast = PO.Utils.query("#po-toast");
      if (!toast) {
        toast = document.createElement("div");
        toast.id = "po-toast";
        toast.className = "po-toast";
        document.body.appendChild(toast);
      }

      toast.className = `po-toast po-toast-${type} is-visible`;
      toast.textContent = message;

      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.hide(), duration);
    },

    hide() {
      const toast = PO.Utils.query("#po-toast");
      if (toast) toast.classList.remove("is-visible");
    }
  };

  // ==========================================================================
  // 12. FORM HELPERS
  // ==========================================================================

  PO.Forms = {
    readFields(root = document) {
      const values = {};
      PO.Utils.queryAll(PO.Constants.selectors.field, root).forEach((el) => {
        const key = el.dataset.poField;
        if (!key) return;

        if (el.type === "checkbox") {
          values[key] = el.checked;
        } else if (el.type === "number") {
          values[key] = el.value === "" ? "" : Number(el.value);
        } else {
          values[key] = el.value;
        }
      });

      return values;
    },

    setChoice(group, value) {
      const path = `answers.${group}`;
      PO.State.set(path, value);

      PO.Utils.queryAll(`[data-po-group="${group}"]`).forEach((el) => {
        el.classList.toggle("is-selected", el.dataset.poValue === value);
      });
    },

    getChoice(group) {
      return PO.State.get(`answers.${group}`, null);
    },

    collectAnswers() {
      return PO.State.get("answers", {});
    },

    clearAnswers() {
      PO.State.set("answers", {});
    }
  };

  // ==========================================================================
  // 13. CHILDREN MANAGER
  // ==========================================================================

  PO.Children = {
    all() {
      return PO.State.get("children", []);
    },

    active() {
      const activeId = PO.State.get("activeChildId");
      return this.all().find((child) => child.id === activeId) || this.all()[0] || null;
    },

    create(data = {}) {
      const children = this.all();
      if (children.length >= PO.Constants.maxChildrenV1) {
        PO.Toast.show("V1 supports up to 8 child profiles.", "warning");
        return null;
      }

      const child = {
        id: PO.Utils.uid("child"),
        name: PO.Utils.compactText(data.name, "Child"),
        age: data.age || "",
        grade: data.grade || "",
        color: PO.Constants.childColors[children.length % PO.Constants.childColors.length],
        mainChallenge: data.mainChallenge || "",
        strengths: data.strengths || [],
        temperament: data.temperament || {},
        notes: data.notes || "",
        createdAt: PO.Utils.now(),
        updatedAt: PO.Utils.now()
      };

      PO.State.set("children", children.concat([child]));
      PO.State.set("activeChildId", child.id);
      PO.State.persistCore();
      PO.Events.emit("child:created", { child });
      return child;
    },

    update(childId, patch = {}) {
      const children = this.all();
      const next = children.map((child) => {
        if (child.id !== childId) return child;
        return {
          ...child,
          ...patch,
          updatedAt: PO.Utils.now()
        };
      });

      PO.State.set("children", next);
      PO.State.persistCore();

      const child = next.find((item) => item.id === childId);
      PO.Events.emit("child:updated", { child });
      return child;
    },

    remove(childId) {
      const children = this.all().filter((child) => child.id !== childId);
      PO.State.set("children", children);

      const active = PO.State.get("activeChildId");
      if (active === childId) {
        PO.State.set("activeChildId", children[0]?.id || null);
      }

      PO.State.persistCore();
      PO.Events.emit("child:removed", { childId });
    },

    switch(childId) {
      const child = this.all().find((item) => item.id === childId);
      if (!child) return;

      PO.State.set("activeChildId", childId);
      PO.Storage.set(PO.Constants.storageKeys.activeChildId, childId);
      PO.Events.emit("child:switched", { child });
      PO.App.render();
    }
  };

  // ==========================================================================
  // 14. HISTORY + FEEDBACK
  // ==========================================================================

  PO.History = {
    all() {
      return PO.Storage.get(PO.Constants.storageKeys.history, []);
    },

    add(entry = {}) {
      const item = {
        id: PO.Utils.uid("history"),
        childId: entry.childId || PO.State.get("activeChildId"),
        situation: entry.situation || PO.State.get("activeSituation"),
        planId: entry.planId || entry.id || null,
        title: entry.title || "",
        createdAt: PO.Utils.now(),
        status: entry.status || "created",
        meta: entry.meta || {}
      };

      return PO.Storage.push(PO.Constants.storageKeys.history, item);
    },

    forChild(childId) {
      return this.all().filter((item) => item.childId === childId);
    },

    recent(limit = 5) {
      return this.all().slice(-limit).reverse();
    }
  };

  PO.Feedback = {
    all() {
      return PO.Storage.get(PO.Constants.storageKeys.feedback, []);
    },

    add(data = {}) {
      const item = {
        id: PO.Utils.uid("feedback"),
        childId: data.childId || PO.State.get("activeChildId"),
        situation: data.situation || PO.State.get("activeSituation"),
        planId: data.planId || PO.State.get("activePlan.id", null),
        result: data.result || "unknown",
        notes: data.notes || "",
        createdAt: PO.Utils.now()
      };

      PO.Storage.push(PO.Constants.storageKeys.feedback, item);
      PO.Events.emit("feedback:added", item);
      return item;
    },

    forChild(childId) {
      return this.all().filter((item) => item.childId === childId);
    },

    needsFollowUp() {
      const history = PO.History.recent(3);
      const feedback = this.all();
      return history.find((h) => !feedback.some((f) => f.planId && f.planId === h.planId));
    }
  };

  // ==========================================================================
  // 15. DASHBOARD
  // ==========================================================================

  PO.Dashboard = {
    render() {
      const children = PO.Children.all();
      const child = PO.Children.active();

      if (!children.length) {
        return PO.Components.shell(
          PO.Components.emptyState(
            "Let's create your first child profile.",
            "Start with one child and one situation. The app can learn more over time.",
            "Add Child",
            "go-child-profile"
          ),
          { showBack: false }
        );
      }

      const followUp = PO.Feedback.needsFollowUp();
      const greeting = `Good ${PO.Utils.formatTimeOfDay()}.`;
      const childCards = children.map((item) => this.childCard(item, child?.id === item.id)).join("");

      const content = `
        ${PO.Components.pageTitle(greeting, "What do you need help with today?")}
        ${followUp ? this.followUpCard(followUp) : ""}
        <section class="po-dashboard-grid">
          <div class="po-dashboard-main">
            ${this.activeChildCard(child)}
            ${this.situationQuickStart()}
          </div>
          <aside class="po-dashboard-side">
            ${PO.Components.card(`
              <div class="po-section-title">
                <h3>Children</h3>
                <button data-po-action="go-child-profile">Add</button>
              </div>
              <div class="po-child-list">${childCards}</div>
            `)}
            ${this.learnedCard(child)}
          </aside>
        </section>
      `;

      return PO.Components.shell(content, { showBack: false });
    },

    childCard(child, active = false) {
      return `
        <button class="po-child-row${active ? " is-active" : ""}" data-po-action="switch-child" data-po-child-id="${PO.Utils.escapeHTML(child.id)}">
          ${PO.Components.childAvatar(child)}
          <span>
            <strong>${PO.Utils.escapeHTML(child.name)}</strong>
            <small>${child.age ? `Age ${PO.Utils.escapeHTML(child.age)}` : "Profile started"}</small>
          </span>
        </button>
      `;
    },

    activeChildCard(child) {
      if (!child) return "";
      const challenge = child.mainChallenge ? PO.Utils.titleCase(child.mainChallenge) : "Not selected yet";
      return PO.Components.card(`
        <div class="po-active-child">
          ${PO.Components.childAvatar(child)}
          <div>
            <span class="po-muted">Working with</span>
            <h2>${PO.Utils.escapeHTML(child.name)}</h2>
            <p>Current focus: ${PO.Components.pill(challenge, "sage")}</p>
          </div>
        </div>
      `, { className: "po-card-hero" });
    },

    situationQuickStart() {
      const situations = [
        ["bedtime", "Bedtime"],
        ["not-listening", "Not listening"],
        ["screens", "Screen time"],
        ["homework", "Homework"],
        ["morning", "Morning routine"],
        ["siblings", "Sibling fight"],
        ["arguing", "Arguing"],
        ["meltdown", "Meltdown"]
      ];

      return PO.Components.card(`
        <div class="po-section-title">
          <h3>Choose a situation</h3>
          <span>Get a short plan</span>
        </div>
        <div class="po-situation-grid">
          ${situations.map(([value, label]) => `
            <button class="po-situation" data-po-action="select-situation" data-po-value="${value}">
              <span>${PO.Utils.escapeHTML(label)}</span>
              <small>${PO.Components.icon("arrow")}</small>
            </button>
          `).join("")}
        </div>
      `);
    },

    learnedCard(child) {
      if (!child) return "";
      const feedbackCount = PO.Feedback.forChild(child.id).length;
      const historyCount = PO.History.forChild(child.id).length;

      return PO.Components.card(`
        <div class="po-section-title">
          <h3>What we're learning</h3>
        </div>
        <div class="po-insight-list">
          <div>${PO.Components.icon("check")} ${historyCount || 0} ${PO.Utils.pluralize(historyCount, "plan")} created</div>
          <div>${PO.Components.icon("check")} ${feedbackCount || 0} feedback ${feedbackCount === 1 ? "entry" : "entries"}</div>
          <div>${PO.Components.icon("spark")} More feedback makes the guidance better.</div>
        </div>
      `);
    },

    followUpCard(item) {
      return PO.Components.card(`
        <div class="po-followup">
          <div>
            <strong>Quick check-in</strong>
            <p>Did the last plan help?</p>
          </div>
          <button class="po-btn po-btn-secondary" data-po-action="go-feedback" data-po-plan-id="${PO.Utils.escapeHTML(item.planId || "")}">Give feedback</button>
        </div>
      `, { className: "po-card-followup" });
    }
  };

  // ==========================================================================
  // 16. SCREENS
  // ==========================================================================

  PO.Screens = {
    welcome() {
      const content = `
        <section class="po-welcome">
          ${PO.Components.eyebrow("Parenting Optimize")}
          <h1>Parenting guidance that fits your family.</h1>
          <p>Build a short profile, choose what is happening, and get a calm plan you can actually use tonight.</p>
          <div class="po-welcome-actions">
            ${PO.Components.button("Start Family Check", "go-parent-profile", { block: true })}
            ${PO.Components.ghostButton("I already started", "go-dashboard", { block: true })}
          </div>
          <div class="po-trust-row">
            <span>No judgement</span>
            <span>Practical steps</span>
            <span>Learns over time</span>
          </div>
        </section>
      `;
      return PO.Components.shell(content, { showBack: false });
    },

    parentProfile() {
      const answers = PO.State.get("answers", {});
      const options = [
        { label: "I become firmer", value: "firm" },
        { label: "I explain again", value: "explain" },
        { label: "I repeat myself", value: "repeat" },
        { label: "I eventually give in", value: "give-in" },
        { label: "It depends on the day", value: "variable" }
      ];

      const content = `
        ${PO.Components.progress(18, "Step 1 of 5")}
        ${PO.Components.pageTitle("When your child ignores you, what usually happens?", "This helps the plan fit your natural parenting style.")}
        ${PO.Components.choiceGrid(options, "parentResponse", answers.parentResponse)}
        <div class="po-actions">
          ${PO.Components.button("Continue", "save-parent-profile", { block: true })}
        </div>
      `;

      return PO.Components.shell(content);
    },

    familyProfile() {
      const answers = PO.State.get("answers", {});
      const options = [
        { label: "Single parent", value: "single" },
        { label: "Two parents together", value: "two-together" },
        { label: "Shared custody", value: "shared" },
        { label: "Other family setup", value: "other" }
      ];

      const content = `
        ${PO.Components.progress(35, "Step 2 of 5")}
        ${PO.Components.pageTitle("What does parenting look like in your home?", "This helps us avoid advice that assumes the wrong setup.")}
        ${PO.Components.choiceGrid(options, "familyStructure", answers.familyStructure)}
        <div class="po-actions">
          ${PO.Components.button("Continue", "save-family-profile", { block: true })}
        </div>
      `;

      return PO.Components.shell(content);
    },

    childProfile() {
      const content = `
        ${PO.Components.progress(52, "Step 3 of 5")}
        ${PO.Components.pageTitle("Create a child profile.", "Start with one child. You can add more later.")}
        <div class="po-form-card">
          ${PO.Components.input("Child name or nickname", "childName", { placeholder: "Example: Ben" })}
          ${PO.Components.input("Age", "childAge", { type: "number", placeholder: "Example: 8" })}
          ${PO.Components.input("Grade or stage", "childGrade", { placeholder: "Example: 3rd grade" })}
        </div>
        <div class="po-actions">
          ${PO.Components.button("Continue", "save-child-basic", { block: true })}
        </div>
      `;

      return PO.Components.shell(content);
    },

    childStrengths() {
      const answers = PO.State.get("answers", {});
      const options = [
        { label: "Creative", value: "creative" },
        { label: "Technical / builder", value: "technical" },
        { label: "Sensitive", value: "sensitive" },
        { label: "Strong-willed", value: "strong-willed" },
        { label: "Funny", value: "funny" },
        { label: "Helpful", value: "helpful" },
        { label: "Organized", value: "organized" },
        { label: "Energetic", value: "energetic" }
      ];

      const content = `
        ${PO.Components.progress(70, "Step 4 of 5")}
        ${PO.Components.pageTitle("What stands out about this child?", "Choose the best fit for now. The app can learn more later.")}
        ${PO.Components.choiceGrid(options, "childStrength", answers.childStrength)}
        <div class="po-actions">
          ${PO.Components.button("Continue", "save-child-strength", { block: true })}
        </div>
      `;

      return PO.Components.shell(content);
    },

    mainChallenge() {
      const answers = PO.State.get("answers", {});
      const options = [
        { label: "Not listening", value: "not-listening" },
        { label: "Bedtime", value: "bedtime" },
        { label: "Screen battles", value: "screens" },
        { label: "Homework", value: "homework" },
        { label: "Sibling conflict", value: "siblings" },
        { label: "Morning routine", value: "morning" },
        { label: "Arguing", value: "arguing" },
        { label: "Emotional blowups", value: "meltdown" }
      ];

      const content = `
        ${PO.Components.progress(88, "Step 5 of 5")}
        ${PO.Components.pageTitle("What should we help with first?", "Pick the situation that feels most urgent.")}
        ${PO.Components.choiceGrid(options, "mainChallenge", answers.mainChallenge)}
        <div class="po-actions">
          ${PO.Components.button("Build My Dashboard", "complete-onboarding", { block: true })}
        </div>
      `;

      return PO.Components.shell(content);
    },

    situationPicker() {
      return PO.Dashboard.render();
    },

    situationDetail() {
      const situation = PO.State.get("activeSituation", "");
      const title = PO.Utils.titleCase(situation);
      const content = `
        ${PO.Components.pageTitle(title, "Answer one quick question so the plan is more useful.")}
        ${PO.Components.choiceGrid([
          { label: "Low intensity", value: "low" },
          { label: "Moderate", value: "moderate" },
          { label: "High intensity", value: "high" },
          { label: "I need help right now", value: "urgent" }
        ], "situationIntensity", PO.Forms.getChoice("situationIntensity"))}
        <div class="po-actions">
          ${PO.Components.button("Show My Plan", "generate-plan", { block: true })}
        </div>
      `;
      return PO.Components.shell(content);
    },

    plan() {
      const plan = PO.State.get("activePlan");
      if (!plan) {
        return PO.Components.shell(
          PO.Components.emptyState("No plan yet.", "Choose a situation first.", "Choose Situation", "go-dashboard")
        );
      }

      const content = `
        ${PO.Components.pageTitle(plan.title || "Tonight's Plan", plan.subtitle || "A short plan based on what we know so far.")}
        ${PO.Components.card(`
          <div class="po-plan-meta">
            ${PO.Components.pill(plan.situationLabel || "Situation", "sage")}
            ${PO.Components.pill(plan.estimatedTime || "3 minutes", "sand")}
          </div>
          <h2>Try this first</h2>
          <ol class="po-plan-steps">
            ${(plan.steps || []).map((step) => `<li>${PO.Utils.escapeHTML(step)}</li>`).join("")}
          </ol>
        `, { className: "po-card-plan" })}
        ${PO.Components.card(`
          <h3>Why this may fit</h3>
          <p>${PO.Utils.escapeHTML(plan.why || "This plan is based on the child profile, parent style, family setup, and the situation selected.")}</p>
        `)}
        ${PO.Components.card(`
          <h3>Avoid for now</h3>
          <ul class="po-avoid-list">
            ${(plan.avoid || []).map((item) => `<li>${PO.Utils.escapeHTML(item)}</li>`).join("")}
          </ul>
        `)}
        <div class="po-actions">
          ${PO.Components.button("I'll Try This", "accept-plan", { block: true })}
          ${PO.Components.ghostButton("Give Feedback Now", "go-feedback", { block: true })}
        </div>
      `;
      return PO.Components.shell(content);
    },

    feedback() {
      const content = `
        ${PO.Components.pageTitle("How did it go?", "Your feedback helps the app learn what works for this child.")}
        ${PO.Components.choiceGrid([
          { label: "Worked well", value: "worked" },
          { label: "Helped a little", value: "somewhat" },
          { label: "Didn't work", value: "did-not-work" },
          { label: "Made it worse", value: "worse" },
          { label: "Didn't try it", value: "not-tried" }
        ], "feedbackResult", PO.Forms.getChoice("feedbackResult"))}
        ${PO.Components.textarea("Optional note", "feedbackNotes", { placeholder: "What happened?" })}
        <div class="po-actions">
          ${PO.Components.button("Save Feedback", "save-feedback", { block: true })}
        </div>
      `;
      return PO.Components.shell(content);
    },

    insights() {
      const child = PO.Children.active();
      const feedback = child ? PO.Feedback.forChild(child.id) : [];
      const history = child ? PO.History.forChild(child.id) : [];

      const content = `
        ${PO.Components.pageTitle("What we're learning", child ? `Insights for ${child.name}` : "Insights")}
        <div class="po-stats-grid">
          ${PO.Components.stat("Plans", history.length)}
          ${PO.Components.stat("Feedback", feedback.length)}
          ${PO.Components.stat("Active child", child?.name || "None")}
        </div>
        ${PO.Components.card(`
          <h3>Early patterns</h3>
          <p>V1 will begin with simple learning. As feedback grows, this area will show what strategies seem to help most.</p>
        `)}
        <div class="po-actions">
          ${PO.Components.button("Back to Dashboard", "go-dashboard", { block: true })}
        </div>
      `;
      return PO.Components.shell(content);
    },

    settings() {
      const content = `
        ${PO.Components.pageTitle("Settings", "Manage this browser's local app data.")}
        ${PO.Components.card(`
          <h3>Local prototype data</h3>
          <p>This V1 stores profiles in this browser using local storage.</p>
          ${PO.Components.ghostButton("Reset Local Data", "reset-app", { block: true })}
        `)}
      `;
      return PO.Components.shell(content);
    }
  };

  // ==========================================================================
  // 17. ENGINE BRIDGE
  // ==========================================================================

  PO.EngineBridge = {
    generatePlan() {
      const child = PO.Children.active();
      const parent = PO.State.get("parent", {});
      const family = PO.State.get("family", {});
      const situation = PO.State.get("activeSituation", "general");
      const answers = PO.Forms.collectAnswers();

      if (PO.Engine && typeof PO.Engine.generatePlan === "function") {
        return PO.Engine.generatePlan({
          child,
          parent,
          family,
          situation,
          answers,
          history: PO.History.forChild(child?.id),
          feedback: PO.Feedback.forChild(child?.id)
        });
      }

      const label = PO.Utils.titleCase(situation);
      return {
        id: PO.Utils.uid("plan"),
        title: `${label}: a short plan for tonight`,
        subtitle: "V1 placeholder output until parenting-engine.js is connected.",
        situation,
        situationLabel: label,
        estimatedTime: "3 minutes",
        steps: [
          "Say the instruction once in a calm, clear sentence.",
          "Offer two acceptable choices rather than opening a debate.",
          "Follow through with the next step without repeating yourself."
        ],
        why: "The app shell is working. The deeper recommendation logic will come from parenting-engine.js and parenting-knowledge.js.",
        avoid: [
          "Avoid repeating the same instruction many times.",
          "Avoid adding new consequences in the middle of the conflict.",
          "Avoid turning the moment into a long lecture."
        ]
      };
    }
  };

  // ==========================================================================
  // 18. ACTIONS
  // ==========================================================================

  PO.Actions = {
    handlers: {},

    register(name, handler) {
      this.handlers[name] = handler;
    },

    run(name, event, el) {
      const handler = this.handlers[name];
      if (!handler) {
        console.warn(`No PO action registered for "${name}"`);
        return;
      }

      try {
        handler(event, el);
      } catch (err) {
        console.error(`PO action failed: ${name}`, err);
        PO.Toast.show("Something went wrong. Please try again.", "error");
      }
    },

    bindDefaults() {
      this.register("back", () => PO.Router.back());
      this.register("close-modal", () => PO.Modal.close());
      this.register("open-settings", () => PO.Router.navigate(PO.Constants.routes.settings));
      this.register("go-dashboard", () => PO.Router.navigate(PO.Constants.routes.dashboard));
      this.register("go-parent-profile", () => PO.Router.navigate(PO.Constants.routes.parentProfile));
      this.register("go-child-profile", () => PO.Router.navigate(PO.Constants.routes.childProfile));
      this.register("go-feedback", () => PO.Router.navigate(PO.Constants.routes.feedback));

      this.register("select-choice", (event, el) => {
        PO.Forms.setChoice(el.dataset.poGroup, el.dataset.poValue);
      });

      this.register("save-parent-profile", () => {
        const response = PO.Forms.getChoice("parentResponse");
        if (!response) return PO.Toast.show("Please choose one option.", "warning");

        PO.State.update("parent", {
          responseStyle: response,
          updatedAt: PO.Utils.now()
        });
        PO.State.persistCore();
        PO.Router.navigate(PO.Constants.routes.familyProfile);
      });

      this.register("save-family-profile", () => {
        const structure = PO.Forms.getChoice("familyStructure");
        if (!structure) return PO.Toast.show("Please choose one option.", "warning");

        PO.State.update("family", {
          structure,
          updatedAt: PO.Utils.now()
        });
        PO.State.persistCore();
        PO.Router.navigate(PO.Constants.routes.childProfile);
      });

      this.register("save-child-basic", () => {
        const values = PO.Forms.readFields(PO.UI.root());
        const nameCheck = PO.Validation.childName(values.childName);
        const ageCheck = PO.Validation.age(values.childAge);

        PO.Validation.clearErrors();

        if (nameCheck !== true) {
          PO.Validation.showError("childName", nameCheck);
          return;
        }

        if (ageCheck !== true) {
          PO.Validation.showError("childAge", ageCheck);
          return;
        }

        PO.State.update("answers", {
          childName: values.childName,
          childAge: values.childAge,
          childGrade: values.childGrade
        });

        PO.Router.navigate(PO.Constants.routes.childStrengths);
      });

      this.register("save-child-strength", () => {
        const strength = PO.Forms.getChoice("childStrength");
        if (!strength) return PO.Toast.show("Please choose one option.", "warning");
        PO.Router.navigate(PO.Constants.routes.mainChallenge);
      });

      this.register("complete-onboarding", () => {
        const challenge = PO.Forms.getChoice("mainChallenge");
        const answers = PO.Forms.collectAnswers();

        if (!challenge) return PO.Toast.show("Please choose one situation.", "warning");

        let child = PO.Children.active();

        if (!child || child.name !== answers.childName) {
          child = PO.Children.create({
            name: answers.childName,
            age: answers.childAge,
            grade: answers.childGrade,
            mainChallenge: challenge,
            strengths: answers.childStrength ? [answers.childStrength] : []
          });
        } else {
          PO.Children.update(child.id, {
            mainChallenge: challenge,
            strengths: answers.childStrength ? [answers.childStrength] : child.strengths
          });
        }

        PO.State.update("session", {
          completedOnboarding: true,
          completedAt: PO.Utils.now()
        });
        PO.State.persistCore();
        PO.Toast.show("Dashboard created.", "success");
        PO.Router.navigate(PO.Constants.routes.dashboard);
      });

      this.register("switch-child", (event, el) => {
        PO.Children.switch(el.dataset.poChildId);
      });

      this.register("select-situation", (event, el) => {
        PO.State.set("activeSituation", el.dataset.poValue);
        PO.Forms.setChoice("situationIntensity", null);
        PO.Router.navigate(PO.Constants.routes.situationDetail);
      });

      this.register("generate-plan", () => {
        const intensity = PO.Forms.getChoice("situationIntensity");
        if (!intensity) return PO.Toast.show("Please choose intensity.", "warning");

        const plan = PO.EngineBridge.generatePlan();
        PO.State.set("activePlan", plan);
        PO.History.add({
          planId: plan.id,
          situation: plan.situation,
          title: plan.title,
          meta: { intensity }
        });
        PO.Router.navigate(PO.Constants.routes.plan);
      });

      this.register("accept-plan", () => {
        PO.Toast.show("Saved. We'll ask how it went later.", "success");
        PO.Router.navigate(PO.Constants.routes.dashboard);
      });

      this.register("save-feedback", () => {
        const result = PO.Forms.getChoice("feedbackResult");
        const fields = PO.Forms.readFields(PO.UI.root());

        if (!result) return PO.Toast.show("Please choose how it went.", "warning");

        PO.Feedback.add({
          result,
          notes: fields.feedbackNotes || ""
        });

        PO.Toast.show("Feedback saved.", "success");
        PO.Router.navigate(PO.Constants.routes.insights);
      });

      this.register("reset-app", () => {
        PO.Modal.open({
          title: "Reset local data?",
          body: "<p>This will clear profiles and history from this browser.</p>",
          actions: `
            <button class="po-btn po-btn-danger" data-po-action="confirm-reset">Reset</button>
            <button class="po-btn po-btn-ghost" data-po-action="close-modal">Cancel</button>
          `
        });
      });

      this.register("confirm-reset", () => {
        PO.Modal.close();
        PO.State.reset();
        PO.Forms.clearAnswers();
        PO.Toast.show("Local data reset.", "success");
        PO.Router.navigate(PO.Constants.routes.welcome, { replace: true });
      });
    }
  };

  // ==========================================================================
  // 19. APP
  // ==========================================================================

  PO.App = {
    initialized: false,

    init() {
      if (this.initialized) return;

      PO.State.init();
      PO.Actions.bindDefaults();
      this.bindEvents();
      this.bindStateListeners();

      const hasOnboarded = PO.State.get("session.completedOnboarding", false);
      const hasChildren = PO.Children.all().length > 0;

      if (hasOnboarded && hasChildren) {
        PO.State.set("route", PO.Constants.routes.dashboard, { silent: true });
      } else {
        PO.State.set("route", PO.Constants.routes.welcome, { silent: true });
      }

      this.initialized = true;
      this.render();

      PO.Events.emit("app:ready", {
        version: PO.version,
        state: PO.State.snapshot()
      });
    },

    bindEvents() {
      document.addEventListener("click", (event) => {
        const actionEl = event.target.closest(PO.Constants.selectors.action);
        if (!actionEl) return;

        const action = actionEl.dataset.poAction;
        if (!action) return;

        event.preventDefault();
        PO.Actions.run(action, event, actionEl);
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          PO.Modal.close();
          PO.Toast.hide();
        }
      });

      window.addEventListener("storage", () => {
        PO.Events.emit("external-storage:changed", {});
      });
    },

    bindStateListeners() {
      PO.Events.on("child:created", () => PO.State.persistCore());
      PO.Events.on("child:updated", () => PO.State.persistCore());
      PO.Events.on("feedback:added", () => PO.State.persistSession());
    },

    render() {
      const route = PO.Router.current();
      const screen = PO.Screens[PO.App.screenMethod(route)] || PO.Screens.welcome;
      const html = screen.call(PO.Screens);
      PO.UI.renderScreen(html);
    },

    screenMethod(route) {
      const map = {
        "welcome": "welcome",
        "parent-profile": "parentProfile",
        "family-profile": "familyProfile",
        "child-profile": "childProfile",
        "child-strengths": "childStrengths",
        "main-challenge": "mainChallenge",
        "dashboard": "dashboard",
        "situation-picker": "situationPicker",
        "situation-detail": "situationDetail",
        "plan": "plan",
        "feedback": "feedback",
        "insights": "insights",
        "settings": "settings"
      };

      return map[route] || "welcome";
    }
  };

  // Attach dashboard screen after App definition so it can call Dashboard directly.
  PO.Screens.dashboard = function dashboardScreen() {
    return PO.Dashboard.render();
  };

  // ==========================================================================
  // 20. DEBUG HELPERS
  // ==========================================================================

  PO.Debug = {
    state() {
      console.log(PO.State.snapshot());
      return PO.State.snapshot();
    },

    reset() {
      PO.State.reset();
      PO.Router.restart();
    },

    seed() {
      PO.State.update("parent", { responseStyle: "repeat" });
      PO.State.update("family", { structure: "two-together" });
      const child = PO.Children.create({
        name: "Sample Child",
        age: 8,
        grade: "3rd grade",
        mainChallenge: "bedtime",
        strengths: ["creative"]
      });
      PO.State.update("session", { completedOnboarding: true });
      PO.State.persistCore();
      PO.Router.navigate(PO.Constants.routes.dashboard);
      return child;
    }
  };

  // ==========================================================================
  // 21. BOOTSTRAP
  // ==========================================================================

  document.addEventListener("DOMContentLoaded", () => {
    PO.App.init();
  });

})(window, document);
